import type { TokenType } from './types';

/**
 * Base error class for all ADF parser errors.
 * Provides structured error information for better debugging and error handling.
 */
export class AdfParseError extends Error {
	/** The position in the input where the error occurred (if applicable) */
	readonly position?: number;

	constructor(message: string, position?: number) {
		super(message);
		this.name = 'AdfParseError';
		this.position = position;

		// Maintains proper stack trace for where error was thrown (V8 engines)
		if (Error.captureStackTrace) {
			Error.captureStackTrace(this, this.constructor);
		}
	}
}

/**
 * Base error class for tokenizer-specific errors.
 * These occur during the lexical analysis phase before parsing.
 */
export class AdfTokenizerError extends AdfParseError {
	constructor(message: string, position: number) {
		super(message, position);
		this.name = 'AdfTokenizerError';
	}
}

/**
 * Error thrown when the parser encounters an unexpected token.
 * This typically occurs during token expectation when the actual token
 * doesn't match the expected token type.
 */
export class AdfUnexpectedTokenError extends AdfParseError {
	/** The token type that was expected */
	readonly expected: TokenType;
	/** The token type that was actually found */
	readonly actual: TokenType;

	constructor(expected: TokenType, actual: TokenType, position: number) {
		super(`Expected ${expected}, got ${actual} at position ${position}`, position);
		this.name = 'AdfUnexpectedTokenError';
		this.expected = expected;
		this.actual = actual;
	}
}

/**
 * Error thrown when the parser expects a delimiter (brace or bracket) or STRING
 * but encounters an unexpected token. This is a specialized case of unexpected
 * token error for object/array parsing contexts.
 */
export class AdfUnexpectedDelimiterError extends AdfParseError {
	/** The delimiter that was expected (LEFT_BRACE or LEFT_BRACKET) */
	readonly expectedDelimiter: 'LEFT_BRACE' | 'LEFT_BRACKET';
	/** The token type that was actually found */
	readonly actual: TokenType;

	constructor(
		expectedDelimiter: 'LEFT_BRACE' | 'LEFT_BRACKET',
		actual: TokenType,
		position: number,
	) {
		super(
			`Expected ${expectedDelimiter} or STRING, got ${actual} at position ${position}`,
			position,
		);
		this.name = 'AdfUnexpectedDelimiterError';
		this.expectedDelimiter = expectedDelimiter;
		this.actual = actual;
	}
}

/**
 * Error thrown when the parser is in an invalid state for the requested operation.
 * For example, calling processChunk() after the final chunk has been signaled.
 */
export class AdfParserStateError extends AdfParseError {
	constructor(message: string) {
		super(message);
		this.name = 'AdfParserStateError';
	}
}

/**
 * Error thrown when the tokenizer encounters an invalid number literal.
 * This occurs when a number doesn't conform to JSON number syntax.
 */
export class AdfInvalidNumberError extends AdfTokenizerError {
	constructor(position: number) {
		super(`Invalid number at position ${position}`, position);
		this.name = 'AdfInvalidNumberError';
	}
}

/**
 * Error thrown when the tokenizer encounters an unexpected character.
 * This occurs when a character cannot be part of any valid JSON token.
 */
export class AdfUnexpectedCharacterError extends AdfTokenizerError {
	/** The unexpected character that was encountered */
	readonly character: string;

	constructor(character: string, position: number) {
		super(`Unexpected character '${character}' at position ${position}`, position);
		this.name = 'AdfUnexpectedCharacterError';
		this.character = character;
	}
}
